import React from 'react'
import { graphql, Link } from 'gatsby'
import styled from 'styled-components'

import SEO from '@components/SEO'
import Layout from '@components/Layout'
import Section from '@components/Section'
import Heading from '@components/Heading'
import LayoutHeroMobile from '@components/Layout/Layout.Hero.Mobile'
import ScrollIndicator from '@components/ScrollIndicator'
import Pill from '@components/Pill'
import Transitions from '@components/Transitions'

import media from '@styles/media'

import LabsPreview from '../../sections/labs/Labs.Preview'
import LabsPreviewMobile from '../../sections/labs/Labs.Preview.Mobile'

/**
 * The labs page is a bit of a mess with the inlined Product Array but it
 * works for now. In the event that we redesign this page it's recommended
 * to start a new file to handle things neater!
 */

function LabsPage({ data, location }) {
  const {
    allContentfulPage,
  } = data
  const { seo } = allContentfulPage.edges[0].node
  const pageBackground =
    'linear-gradient(180deg, #08070B 40%, #111216 50%, #191D23 100%)'
  const navConfig = {
    fixed: true,
    theme: 'light',
    offset: true,
  }
  const footerConfig = {
    visible: true,
  }

  // Inlining our products to get the right variables we need in scope!
  const products = [
    {
      logo: SwasthaIDLogo,
      background: '/images/apps/swasthaid-logo-d.png',
      backgroundColor: '#0a1628',
      excerpt:
        "Nepal's first inter-hospital health data network. Secure, instant sharing of medical records with patients controlling their own health data.",
      children: (
        <>
          <HorizontalRule dark />
          <LinkToProduct dark as="div">
            Contact us to learn more about SwasthaID
          </LinkToProduct>
        </>
      ),
    },
    {
      logo: RevvlowLogo,
      background: '/images/apps/revvlow.jpg',
      backgroundColor: '#1a0a08',
      excerpt:
        "Monetize unsold inventory in real time with dynamic pricing for cinemas, events & hospitality — zero disruption to your primary pricing strategy.",
      children: (
        <>
          <HorizontalRule dark />
          <LinkToProduct dark as="div">
            Contact us to learn more about Revvlow
          </LinkToProduct>
        </>
      ),
    },
    {
      logo: OsadAILogo,
      background: '/images/apps/osadai.png',
      backgroundColor: '#071a0a',
      excerpt:
        "AI-driven natural medicine & wellness platform combining Ayurveda with modern AI formulation — scientifically-backed nutraceutical solutions.",
      children: (
        <>
          <HorizontalRule dark />
          <LinkToProduct dark as="div">
            Contact us to learn more about OsadAI
          </LinkToProduct>
        </>
      ),
    },
  ]

  return (
    <Layout nav={navConfig} background={pageBackground} footer={footerConfig}>
      <>
        <SEO
          title={seo.title}
          description={seo.description}
          image={seo.image.file.url}
          pathname={location.pathname}
        />
        <LayoutHeroMobile>
          <HeroSection hideOverflow>
            <ContentContainer>
              <div />
              <Transitions.CSS.FadeIn>
                <TextContainer>
                  <Pill text="Strategic Innovation" />
                  <Heading.h2 styles="h1">
                    Pioneering High-Impact Technology for Health and Ecology.
                  </Heading.h2>
                  <MainText>
                    We are developing the core infrastructure for the next generation of
                    natural medicine discovery, secure medical data exchange, and
                    systemic sustainability.
                  </MainText>
                </TextContainer>
              </Transitions.CSS.FadeIn>
              <ScrollIndicator />
            </ContentContainer>

          </HeroSection>
        </LayoutHeroMobile>
        <HeroImageMobile>
        </HeroImageMobile>
        <LabsBody>
          <Section narrow>
            {products.map(product => (
              <LabsPreview key={product.excerpt} product={product} />
            ))}
          </Section>
          <Section narrow>
            <LabsPreviewMobile />
          </Section>
        </LabsBody>
      </>
    </Layout>
  )
}

export default LabsPage

export const pageQuery = graphql`
  query LabsPageQuery {
    allContentfulPage(filter: { pageName: { eq: "Labs" } }) {
      edges {
        node {
          seo {
            title
            description
            image {
              file {
                url
              }
            }
          }
        }
      }
    }
  }
`

const HeroSection = styled(Section)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.tablet`
    pointer-events: none;
  `};
`

const HeroImage = styled.div`
  position: relative;
  width: 610px;
  top: -180px;
  right: -40px;
  animation: float 4.8s ease-in-out infinite 1.4s;

  @keyframes float {
    0% {
      transform: translatey(0px);
    }
    50% {
      transform: translatey(-8px);
    }
    100% {
      transform: translatey(0px);
    }
  }

  ${media.tablet`
    display: none;
  `};

  .gatsby-image-wrapper {
    position: absolute !important;
    top: -180px;
    left: 0;
    width: 100%;
  }
`

const HeroImageMobile = styled(HeroImage)`
  display: none;

  ${media.tablet`
    box-shadow: 0px -40px 40px rgba(8, 7, 11, 0.8);
    display: block;
    width: 100%;
    top: 0;
    right: 0;
    margin-bottom: 60px;
  `};

  .gatsby-image-wrapper {
    position: relative !important;
    top: 0;
    left: 0;
    width: 100%;
  }
`

const TextContainer = styled.div`
  max-width: 560px;

  ${media.phablet`
    position: relative;
    top: -50px;
  `}
`

const MainText = styled.p`
  font-size: 3.2rem;
  font-weight: 400;
  color: ${p => p.theme.colors.grey};
  line-height: 1.3;

  ${media.phablet`
    font-size: 2.2rem;
  `};
`

const ContentContainer = styled.div`
  height: calc(100vh - 140px);
  min-height: 440px;

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${media.tablet`
    height: calc(100vh - 90px);
    padding: 0;
    user-select: none;
    pointer-events: none;
  `};
`

const HorizontalRule = styled.hr`
  width: 140px;
  height: 1px;
  border: none;
  margin-bottom: 30px;
  background: ${p => (p.dark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255,255,255,0.1)')};

  ${media.phablet`
    width: 100%;
    margin: 0 auto 25px;
    background: ${p =>
      p.dark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255,255,255,0.1)'};
  `}
`

const LabsBody = styled.div`
  position: relative;
  z-index: 1;
`

const LinkToProduct = styled.a`
  position: relative;
  font-weight: 600;
  font-size: 16px;
  color: ${p => (p.dark ? p.theme.colors.grey : '#fff')};

  svg {
    margin-right: 13px;
  }

  &:nth-child(2) {
    margin-left: 30px;
  }

  &[data-a11y='true']:focus::after {
    content: '';
    position: absolute;
    left: -10%;
    top: -42%;
    width: 120%;
    height: 200%;
    border: 2px solid ${p => p.theme.colors.purple};
    background: rgba(255, 255, 255, 0.01);
    border-radius: 5px;
  }

  ${media.tablet`
    display: block;
    margin: 0 auto;
    color: ${p => (p.dark ? '#000' : '#fff')};

    &:nth-child(2) {
      margin: 15px auto 0;
    }

      svg {
        display: none;
      }
  `}
`

const Symbol = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  text-align: center;
  border-radius: 2.5px;
  padding: 1px 2px;
  color: ${p => p.theme.colors.bg};
  background: ${p => p.theme.colors.moon};
  font-size: 12px;
  font-weight: 400;
  margin-left: 5px;
`

const SwasthaIDLogo = () => (
  <img src="/images/apps/swasthaid-logo.png" alt="SwasthaID" style={{ height: '40px', width: 'auto' }} />
)

const RevvlowLogo = () => (
  <img src="/images/apps/revvlow-logo.svg" alt="Revvlow" style={{ height: '32px', width: 'auto' }} onError={(e) => { (e.target as HTMLImageElement).src = '/images/apps/revvlow.jpg' }} />
)

const OsadAILogo = () => (
  <OsadLogoWrap>
    <OsadLeaf />
    <OsadWordmark>OsadAI</OsadWordmark>
  </OsadLogoWrap>
)

const OsadLogoWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const OsadLeaf = styled.div`
  width: 28px;
  height: 28px;
  background: #2d7a3a;
  border-radius: 50% 0 50% 0;
  transform: rotate(45deg);
  flex-shrink: 0;
`

const OsadWordmark = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: #2d7a3a;
  letter-spacing: -0.5px;
`


